import React, { FC, useCallback, useEffect, useState } from "react";
import Button, { ButtonVariants } from "../Display/Button";
import Toggle from "../DataInput/Toggle";
import Select from "../DataInput/Select";
import NumberInput from "../DataInput/NumberInput";
import { SettingsCaption, SettingsControlField, SettingsFooter, SettingsGroup, SettingsHeader, SettingsPanel, SettingsPanelSkeleton, SettingsRow } from "../Settings";
import { getMyDialerSettings, updateMyDialerSettings } from "../../services/firebase";
import PlanUpgradePrompt from "../Billing/PlanUpgradePrompt";
import { DialerSettings, MyDialerSettings, MyDialerResult } from "../../types/dialer";

/**
 * A rep's own dialing preferences. The organization sets the dispositions and
 * may lock the delay between calls; everything here belongs to the person and
 * follows them to whichever browser they sign in from.
 */
const MyDialerSection: FC = () => {
  const [data, setData] = useState<MyDialerResult | null>(null);
  const [mine, setMine] = useState<MyDialerSettings>({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await getMyDialerSettings();
      setData(r);
      setMine(r.mine || {});
    } catch (e: any) {
      setMessage(e?.message || "Could not load your dialer preferences.");
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  if (!data && message) return <SettingsCaption>{message}</SettingsCaption>;

  /* The preferences panel: heading, then the row per preference the rep can
     set, each with its switch at the right. */
  if (!data)
    return (
      <SettingsPanelSkeleton
        titleWidth={210}
        captionWidths={["94%", "56%"]}
        rows={[
          { labelWidth: 250, captionWidth: "58%", control: "toggle" },
          { labelWidth: 270, captionWidth: "50%", control: "toggle" },
          { labelWidth: 280, control: "toggle" },
          { labelWidth: 220, control: "toggle" },
          { labelWidth: 320, control: "toggle" },
          { labelWidth: 240, captionWidth: "46%", control: "toggle" },
          { labelWidth: 190, captionWidth: "40%", control: "toggle" },
          { labelWidth: 280, captionWidth: "36%", control: "toggle" },
        ]}
        footerButtons={[232, 88]}
      />
    );

  const effective: DialerSettings = data.effective;
  const delayLocked = data.org.callDelay.adminLocked;

  const patchFlags = (flags: Partial<DialerSettings["flags"]>) => {
    setMine({ ...mine, flags: { ...(mine.flags || {}), ...flags } });
    setDirty(true);
  };
  const patchDelay = (delay: Partial<DialerSettings["callDelay"]>) => {
    setMine({ ...mine, callDelay: { ...(mine.callDelay || {}), ...delay } });
    setDirty(true);
  };
  const patchHours = (hours: Partial<DialerSettings["callingHours"]>) => {
    setMine({ ...mine, callingHours: { ...(mine.callingHours || {}), ...hours } });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const r = await updateMyDialerSettings(mine);
      setData(r);
      setMine(r.mine || {});
      setDirty(false);
      setMessage("Saved.");
    } catch (e: any) {
      setMessage(e?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setMine({});
    setDirty(true);
  };

  const flags = effective.flags;

  return (
    <SettingsPanel>
      <SettingsHeader
        title="My dialing preferences"
        caption={`These follow you, not the organization. Your admin sets the call outcomes everyone logs${delayLocked ? " and has locked the delay between calls" : ""}.`}
      />
      {data.planRequired && <PlanUpgradePrompt feature="dialer" what="Dial faster" />}

      <SettingsGroup>
        <SettingsRow
          label="Handle voicemail automatically"
          caption="Detects the beep and acts without you listening for it."
          control={<Toggle checked={flags.AUTOMATED_VOICEMAIL_FLOW} onChange={(v) => patchFlags({ AUTOMATED_VOICEMAIL_FLOW: v })} />}
        />
        {flags.AUTOMATED_VOICEMAIL_FLOW && (
          <SettingsRow
            label="When a voicemail is detected"
            control={
              <SettingsControlField>
                <Select
                  value={flags.AUTOMATED_VOICEMAIL_BEHAVIOR}
                  onChange={(e: any) => patchFlags({ AUTOMATED_VOICEMAIL_BEHAVIOR: e?.target?.value || e })}
                  options={[
                    { value: "SKIP", label: "Hang up and log no answer" },
                    { value: "DROP", label: "Drop the recorded voicemail" },
                  ]}
                />
              </SettingsControlField>
            }
          />
        )}
        <SettingsRow
          label="Handle gatekeepers automatically"
          caption="Recognises a switchboard and logs the outcome for you."
          control={<Toggle checked={flags.AUTOMATED_GATEKEEPER_FLOW} onChange={(v) => patchFlags({ AUTOMATED_GATEKEEPER_FLOW: v })} />}
        />
        <SettingsRow
          label="Redial calls that drop while ringing"
          control={<Toggle checked={flags.EARLY_CALL_END_REDIAL} onChange={(v) => patchFlags({ EARLY_CALL_END_REDIAL: v })} />}
        />
        <SettingsRow
          label="Lock hang up while ringing"
          control={<Toggle checked={flags.LOCK_HANGUP_WHILE_RINGING} onChange={(v) => patchFlags({ LOCK_HANGUP_WHILE_RINGING: v })} />}
        />
        <SettingsRow
          label="Ask for a number when the prospect has none"
          control={<Toggle checked={flags.NO_NUMBER_ENRICHMENT} onChange={(v) => patchFlags({ NO_NUMBER_ENRICHMENT: v })} />}
        />
        <SettingsRow
          label="Hang up unanswered calls"
          caption="Ends the call once it has rung for this long."
          control={<Toggle checked={flags.RING_TIMEOUT_ENABLED} onChange={(v) => patchFlags({ RING_TIMEOUT_ENABLED: v })} />}
        >
          {flags.RING_TIMEOUT_ENABLED && (
            <NumberInput label="Seconds" value={flags.RING_TIMEOUT_SECONDS} onChange={(v) => patchFlags({ RING_TIMEOUT_SECONDS: v })} />
          )}
        </SettingsRow>
        <SettingsRow
          label="Pause between calls"
          caption={delayLocked ? "Set by your admin." : "A breath between dials."}
          control={<Toggle checked={effective.callDelay.enabled} disabled={delayLocked} onChange={(v) => patchDelay({ enabled: v })} />}
        >
          {effective.callDelay.enabled && !delayLocked && (
            <NumberInput label="Milliseconds" value={effective.callDelay.delayMs} onChange={(v) => patchDelay({ delayMs: v })} />
          )}
        </SettingsRow>
        <SettingsRow
          label="Only dial inside my calling hours"
          caption="Uses your own clock."
          control={<Toggle checked={effective.callingHours.enabled} onChange={(v) => patchHours({ enabled: v })} />}
        >
          {effective.callingHours.enabled && (
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <NumberInput label="From (hour)" value={effective.callingHours.minHour} onChange={(v) => patchHours({ minHour: v })} />
              <NumberInput label="Until (hour)" value={effective.callingHours.maxHour} onChange={(v) => patchHours({ maxHour: v })} />
            </div>
          )}
        </SettingsRow>
      </SettingsGroup>

      <SettingsFooter start={message && <SettingsCaption>{message}</SettingsCaption>}>
        <Button variant={ButtonVariants.light} type="button" onClick={reset}>
          Use the organization defaults
        </Button>
        <Button variant={ButtonVariants.brand} type="button" disabled={!dirty || saving} loading={saving} onClick={save}>
          Save
        </Button>
      </SettingsFooter>
    </SettingsPanel>
  );
};

export default MyDialerSection;
